import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function CategoriesPanel() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", name_ta: "", sort_order: 0 });
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    const r = await api.get("/categories");
    setItems(r.data || []);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name) return toast.error("Enter a name");
    try {
      if (editingId) await api.put(`/categories/${editingId}`, form);
      else await api.post("/categories", form);
      toast.success("Saved");
      setOpen(false); setForm({ name: "", name_ta: "", sort_order: 0 }); setEditingId(null);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Save failed"));
    }
  };

  const edit = (c) => {
    setForm({ name: c.name, name_ta: c.name_ta || "", sort_order: c.sort_order || 0 });
    setEditingId(c.id);
    setOpen(true);
  };

  const del = async (id) => {
    if (!window.confirm("Delete category?")) return;
    await api.delete(`/categories/${id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <div>
          <h2 className="text-xl font-semibold text-charcoal">Categories</h2>
          <p className="text-sm text-charcoal/60">{items.length} categories</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm({ name: "", name_ta: "", sort_order: 0 }); setEditingId(null); } }}>
          <DialogTrigger asChild>
            <Button data-testid="add-category-btn" style={{ backgroundColor: "var(--forest)", color: "white" }}>
              <Plus className="w-4 h-4 mr-1.5" /> New category
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingId ? "Edit" : "Add"} category</DialogTitle>
              <DialogDescription>Categories group products on the storefront. Tamil name is optional.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label>Name (English)</Label>
                <Input data-testid="category-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <Label>பெயர் (Tamil, optional)</Label>
                <Input data-testid="category-name-ta" value={form.name_ta} onChange={(e) => setForm({ ...form, name_ta: e.target.value })} placeholder="பழங்கள்" />
              </div>
              <div>
                <Label>Sort order</Label>
                <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} data-testid="save-category-btn" style={{ backgroundColor: "var(--forest)", color: "white" }}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-black/10 rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Sort order</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((c) => (
              <TableRow key={c.id} data-testid={`category-row-${c.id}`}>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell>{c.sort_order}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => edit(c)}><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => del(c.id)}><Trash2 className="w-3.5 h-3.5 text-terracotta" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
